import React, { useContext } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { Tooltip } from 'react-tooltip';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { useStateSelector } from 'src/redux';
import { formatNumber } from 'src/utils/misc';
import { Variable } from 'src/api/types';
import { VariablesContext } from '../context';
import { getVariableIcon } from './variableIcon';

// Visuals ported from FormulaInput/comps/DecoratorComponent.tsx
const Wrap = styled.span`
  background-color: ${({ theme }) => theme.colors.aliceBlue};
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 10px;
  padding: 0 10px;
  margin: 0 2px;
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;

  * {
    white-space: pre;
  }

  .dc-icon {
    margin-right: 5px;
    transform: translateY(-1px);
    display: inline-flex;
  }

  .dc-no-data {
    transform: scale(0.8) translateX(3px) translateY(-1px);
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding: 0 8px;
    .dc-no-data {
      transform: scale(0.7) translateX(3px) translateY(-1px);
    }
  }
`;

export const VariableChip: React.FC<NodeViewProps> = ({ node }) => {
  const colors = useStateSelector(({ theme }) => theme.colors);
  const { variables, workspaceVariableNames } = useContext(VariablesContext);

  const id = Number(node.attrs.id);
  const ctxVar = variables.find((v) => v.id === id) as Variable | undefined;
  const label = ctxVar?.alias || ctxVar?.name || node.attrs.label || `#${node.attrs.id}`;
  const tooltipId = `fi-chip-${node.attrs.id}`;

  // Live context value is the source of truth; fall back to the value carried on the node
  // (set at insert time after a "+ Add Value" save) to avoid a no-data flicker pre-refetch.
  const data: Variable | undefined = ctxVar
    ? _.isNil(ctxVar.value) && !_.isNil(node.attrs.value)
      ? { ...ctxVar, value: node.attrs.value, currency: node.attrs.currency ?? ctxVar.currency }
      : ctxVar
    : undefined;

  const tooltipValue = _.isNil(data?.value)
    ? `<div style='display: flex; grid-gap: 6px;'>
    <span>No Data</span>
        <svg
        style='transform: scale(0.8)'
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        data-cy="no-data"
      >
        <path
          d="M9.7963 6.48145V9.99997M9.7963 13.5185H9.80509M18.5926 9.99997C18.5926 14.858 14.6544 18.7963 9.7963 18.7963C4.93824 18.7963 1 14.858 1 9.99997C1 5.14191 4.93824 1.20367 9.7963 1.20367C14.6544 1.20367 18.5926 5.14191 18.5926 9.99997Z"
          stroke="${colors.statusAmber}"
          stroke-width="1.75926"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>`
    : formatNumber(data?.value || 0, { symbol: !!data?.currency, maxDecimals: 3 });

  return (
    <NodeViewWrapper as="span" contentEditable={false} style={{ display: 'inline' }}>
      <Tooltip
        id={tooltipId}
        style={{ backgroundColor: colors.prussianBlue, zIndex: 10, color: colors.white }}
        opacity={1}
      />
      <Wrap data-tooltip-id={tooltipId} data-tooltip-html={`${tooltipValue}`}>
        {data && <span className="dc-icon">{getVariableIcon(data, workspaceVariableNames)}</span>}
        {label}
        {_.isNil(data?.value) && (
          <svg
            className="dc-no-data"
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            data-cy="no-data"
          >
            <path
              d="M9.7963 6.48145V9.99997M9.7963 13.5185H9.80509M18.5926 9.99997C18.5926 14.858 14.6544 18.7963 9.7963 18.7963C4.93824 18.7963 1 14.858 1 9.99997C1 5.14191 4.93824 1.20367 9.7963 1.20367C14.6544 1.20367 18.5926 5.14191 18.5926 9.99997Z"
              stroke={colors.statusAmber}
              strokeWidth="1.75926"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </Wrap>
    </NodeViewWrapper>
  );
};
